import { forwardRef, ReactNode, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { ToggleButtonProps } from '@mui/material';

import { UnsubscribeFormProps, UnsubscribeOptions } from './form.models';
import { Form as FormBase, IFormImperativeHandleProps } from '../../../../common/form';
import { ToggleButtonGroup } from '../../../../common/form/toggle-button-group';
import { TextField } from '../../../../common/form/text-field';
import { UNSUBSCRIBE_LENGTH } from '../../../../../constants';
import { yup } from '../../../../../services/yup';
import { useStyles } from './form.styles';

export const Form = forwardRef<
IFormImperativeHandleProps, UnsubscribeFormProps>(({ onSubmit }, ref): JSX.Element => {
  const intl = useIntl();
  const classes = useStyles();

  const defaultValues = useMemo(() => ({ unsubscribeFormOptions: [], other: '' }), []);
  const validationSchema = useMemo(() => yup.object({
    unsubscribeFormOptions: yup.array().of(yup.string().nullable().trim()),
    other: yup.string().nullable().trim(),
  }), [intl]);
  const handleSubmit = () => {
    onSubmit!();
  };

  const formOptions: ToggleButtonProps[] = [{
    children: <FormattedMessage id="unsubscribe.list-1" />,
    value: UnsubscribeOptions.often,
  }, {
    children: <FormattedMessage id="unsubscribe.list-2" />,
    value: UnsubscribeOptions.push,
  }, {
    children: <FormattedMessage id="unsubscribe.list-3" />,
    value: UnsubscribeOptions.notInterested,
  }, {
    children: intl.formatMessage({ id: 'unsubscribe.list-4' }, { sup: (chunks: ReactNode) => <sup>{chunks}</sup> }),
    value: UnsubscribeOptions.stopWearing,
  }, {
    children: intl.formatMessage({ id: 'unsubscribe.list-5' }, {
      sup: (chunks: ReactNode) => <sup>{chunks}</sup>,
      span: (chunks: ReactNode) => <span className={classes.nowrap}>{chunks}</span>,
      wrap: (chunks: ReactNode) => <span>{chunks}</span>,
      noBreak: <>&nbsp;</>,
    }),
    value: UnsubscribeOptions.enoughInfo,
  }, {
    children: <FormattedMessage id="unsubscribe.list-6" />,
    value: UnsubscribeOptions.lazerCorrection,
  }, {
    children: <FormattedMessage id="unsubscribe.list-7" />,
    value: UnsubscribeOptions.other,
  }];

  return (
    <FormBase
      ref={ref}
      defaultValues={defaultValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      className={classes.root}
    >
      <ToggleButtonGroup
        name="unsubscribeFormOptions"
        options={formOptions}
        standalone
        size="small"
        variant="outlined"
        groupStyles={classes.groupStyles}
        formControlStyles={classes.groupWrapper}
      />
      <TextField
        name="other"
        fullWidth
        multiline
        placeholder={intl.formatMessage({ id: 'unsubscribe.list-7.placeholder' })}
        inputProps={{ maxLength: UNSUBSCRIBE_LENGTH }}
        className={classes.other}
      />
    </FormBase>
  );
});
